<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Product' : 'Add New Product'"
      :container-class="null"
      class="mb-16pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Product Images" label-for="images" label-class="form-label">
        <draggable v-if="images.length" class="row mb-3" :list="images" handle=".product-image-drag-handle">
          <div v-for="(productImage, index) in images" :key="productImage" class="col-6 col-md-3 mb-3">
            <div class="card h-100 mb-0 p-2">
              <img :src="productImage" :alt="`Product image ${index + 1}`" class="product-image-preview" />
              <div class="d-flex align-items-center justify-content-between mt-2">
                <button
                  type="button"
                  class="btn btn-sm btn-link text-muted product-image-drag-handle"
                  aria-label="Reorder image"
                >
                  <i class="fas fa-bars"></i>
                </button>
                <b-btn
                  variant="danger"
                  size="sm"
                  @click.prevent="removeImage(index)"
                  :aria-label="`Remove image ${index + 1}`"
                >
                  <i class="material-icons icon-18pt">close</i>
                </b-btn>
              </div>
            </div>
          </div>
        </draggable>

        <image-uploader
          multiple
          @images="uploadImages"
          :isUploading="isImgUploading"
          :isRequired="!images.length"
          :placeholder="isImgUploading ? 'Uploading images...' : 'Select one or more images'"
        />
        <small class="form-text text-muted"
          >The first image is the primary image. Drag images to change their order.</small
        >
        <b-form-invalid-feedback :state="!$v.images.required && $v.images.$dirty ? false : null">
          At least one product image is required.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Product Name" label-for="product_name" label-class="form-label">
        <b-form-input id="name" placeholder="Enter Product's Name" v-model="name" />
        <b-form-invalid-feedback :state="!$v.name.required && $v.name.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label="Product Category" label-for="category" label-class="form-label">
        <v-select
          id="category"
          class="form-control v-select-custom"
          label="text"
          v-model="category"
          :reduce="category => category.value"
          placeholder="Select delivery type"
          :options="categoryOptions"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!category" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.category.required && $v.category.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label="Product Colors" label-for="color" label-class="form-label">
        <div class="d-flex flex-wrap align-items-center">
          <div
            v-for="(productColor, index) in colors"
            :key="index"
            class="color-chip"
            :style="{ '--swatch-color': productColor.hex }"
          >
            <v-swatches
              :value="productColor.hex"
              shapes="circles"
              show-fallback
              :swatches="swatches"
              fallback-input-type="color"
              fallback-ok-text="Select"
              fallback-input-class="py-0"
              @input="updateColor(index, $event)"
            >
              <template v-slot:trigger>
                <button
                  type="button"
                  class="color-chip__dot"
                  :title="productColor.hex"
                  :aria-label="`Change the shade of ${productColor.name}`"
                ></button>
              </template>
            </v-swatches>
            <input
              v-if="editingColorIndex === index"
              ref="colorNameInput"
              type="text"
              class="color-chip__input"
              :value="productColor.name"
              :size="Math.max(productColor.name.length, 4)"
              :aria-label="`Name of color ${productColor.hex}`"
              @keydown.enter.prevent="$event.target.blur()"
              @blur="renameColor(index, $event)"
            />
            <template v-else>
              <span class="color-chip__label" :title="productColor.hex">{{ productColor.name }}</span>
              <button
                type="button"
                class="color-chip__edit"
                @click.prevent="editColorName(index)"
                :aria-label="`Rename color ${productColor.name}`"
              >
                <i class="material-icons icon-16pt">edit</i>
              </button>
            </template>
            <button
              type="button"
              class="color-chip__remove"
              @click.prevent="removeColor(index)"
              :aria-label="`Remove color ${productColor.name}`"
            >
              <i class="material-icons icon-16pt">close</i>
            </button>
          </div>

          <v-swatches
            v-model.trim="newColor"
            shapes="circles"
            show-fallback
            :swatches="swatches"
            fallback-input-type="color"
            fallback-ok-text="Select"
            fallback-input-class="py-0"
            @input="addColor"
          >
            <template v-slot:trigger>
              <button type="button" class="color-chip color-chip--add">
                <i class="material-icons icon-16pt mr-1">add</i>
                Add color
              </button>
            </template>
          </v-swatches>
        </div>
        <small class="form-text text-muted">
          Add every color this product is offered in, and name each one however your buyers should see it. Buyers pick
          one at checkout.
        </small>
      </b-form-group>

      <b-form-group label="Price" label-for="price" label-class="form-label">
        <currency-input
          id="price"
          v-model="price"
          required
          placeholder="Enter product's price"
          :allow-negative="false"
          class="form-control"
          :class="{ 'is-invalid': !$v.price.required && $v.price.$dirty }"
        />
        <b-form-invalid-feedback :state="!$v.price.required && $v.price.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="hideFromStore" name="check-button" switch>
          Hide From School Public Store?
        </b-form-checkbox>
      </b-form-group>

      <!-- <b-form-group label="Size" label-for="size" label-class="form-label">
        <b-form-input id="size" placeholder="Enter Size" v-model="size" type="number" />
      </b-form-group> -->

      <b-form-group label="Description" label-for="description" label-class="form-label">
        <b-form-textarea id="description" placeholder="Enter Product's Description" :rows="5" v-model="description" />
        <b-form-invalid-feedback :state="!$v.description.required && $v.description.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="sizeEnable" name="check-button" switch>Offer size options?</b-form-checkbox>
      </b-form-group>

      <div class="row mb-lg-10pt" v-if="sizeEnable">
        <draggable
          class="col-sm-12 draggable-cursor"
          @start="dragging = true"
          @end="dragging = false"
          :list="sizes"
          :handle="'#handle'"
        >
          <div v-for="(fe, index) in $v.sizes.$each.$iter" :key="index" class="mb-2">
            <div class="d-flex justify-content-center">
              <div class="icon-align mt-2 mr-2">
                <i class="fas fa-bars icon-16pt mr-8pt" id="handle"></i>
              </div>

              <b-form-group class="w-100">
                <b-form-input
                  id="size"
                  placeholder="Enter Size"
                  v-model="sizes[index]"
                  :state="!fe.required && fe.$dirty ? false : null"
                />

                <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
              </b-form-group>

              <div class="icon-align ml-2 mt-1">
                <b-btn
                  v-if="sizes.length > 1"
                  variant="danger"
                  @click.prevent="removeSize(index)"
                  v-b-popover.hover.right
                  :title="'Remove Size'"
                  size="sm"
                >
                  <i class="material-icons icon-18pt">close</i>
                </b-btn>
              </div>
            </div>
          </div>
        </draggable>
      </div>
      <div class="d-flex justify-content-end" v-if="sizeEnable">
        <b-btn @click.prevent="addSize()" size="sm"> <i class="material-icons icon-18pt">add</i>Add More </b-btn>
      </div>

      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>Save Product</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { get } from 'lodash';
import Page from '@/components/Page.vue';
import vSelect from 'vue-select';
import VSwatches from 'vue-swatches';

import draggable from 'vuedraggable';

import { required, requiredIf } from 'vuelidate/lib/validators';
import 'vue-swatches/dist/vue-swatches.css';
import ImageUploader from '@/components/ImageUploader.vue';
import { getColorName } from '@/common/colorNames';

export default {
  components: {
    PageHeader,
    draggable,
    ImageUploader,
    vSelect,
    VSwatches,
  },
  name: 'PostProduct',
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Product' : 'Add Product',
      name: '',
      price: null,

      description: '',
      isLoading: false,
      images: [],
      isImgUploading: false,
      sizes: [''],
      sizeEnable: false,
      hideFromStore: false,
      dragging: false,

      category: null,
      colors: [],
      editingColorIndex: null,
      newColor: null,
      swatches: [
        ['#000000', '#FFFFFF', '#808080', '#C0C0C0'],
        ['#E84B3C', '#F39C19', '#F1C40F', '#2ECC70'],
        ['#1FBC9C', '#3398DB', '#3B86F5', '#8E43AD'],
        ['#795548', '#FFC0CB', '#F5F5DC', '#222F3D'],
      ],
      categoryOptions: [
        { text: 'Apparel', value: 'apparel' },
        { text: 'Accessories', value: 'accessories' },
        { text: 'Classroom Supplies', value: 'classroom_supplies' },
        { text: 'Clinical/Lab Supplies', value: 'clinical_supplies' },
        { text: 'Other', value: 'others' },
      ],
    };
  },
  validations() {
    return {
      name: { required },
      price: { required },
      category: { required },

      description: { required },
      images: { required },
      sizes: {
        $each: { required: requiredIf(() => this.sizeEnable) },
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        {
          text: 'Products',
          to: {
            name: 'manage-product-list',
          },
        },
        {
          text: this.$route.params.id ? 'Edit Product' : 'Add Product',
          active: true,
        },
      ];
    },
    isFormLoading() {
      return this.isLoading || this.isImgUploading;
    },
  },

  methods: {
    ...mapActions('products', ['createProduct', 'updateProduct', 'getProduct', 'getProductImageUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    get,
    getColorName,
    addSize() {
      this.sizes.splice(this.sizes.length, 0, '');
    },

    removeSize(index) {
      this.sizes.splice(index, 1);
    },

    removeImage(index) {
      this.images.splice(index, 1);
    },

    hasColorNamed(name, ignoreIndex = -1) {
      // Buyers pick a color by its name, so two colors on one product cannot share a name.
      return this.colors.some(
        (existing, index) => index !== ignoreIndex && existing.name.toLowerCase() === name.toLowerCase(),
      );
    },

    availableColorName(baseName, ignoreIndex = -1) {
      let name = baseName;
      let suffix = 2;

      while (this.hasColorNamed(name, ignoreIndex)) {
        name = `${baseName} ${suffix}`;
        suffix += 1;
      }

      return name;
    },

    addColor(value) {
      if (value) {
        const hex = value.toUpperCase();
        if (this.colors.some(existing => existing.hex === hex)) {
          this.makeToast({ variant: 'danger', msg: 'That color is already added to this product.' });
        } else {
          this.colors.push({ hex, name: this.availableColorName(getColorName(hex)) });
        }
      }
      this.newColor = null;
    },

    updateColor(index, value) {
      if (!value) return;

      const hex = value.toUpperCase();
      if (hex === this.colors[index].hex) return;

      if (this.colors.some((existing, i) => i !== index && existing.hex === hex)) {
        this.makeToast({ variant: 'danger', msg: 'That color is already added to this product.' });
        return;
      }

      this.$set(this.colors, index, { ...this.colors[index], hex });
    },

    editColorName(index) {
      this.editingColorIndex = index;
      this.$nextTick(() => {
        const [input] = [].concat(this.$refs.colorNameInput || []);
        if (input) input.select();
      });
    },

    renameColor(index, event) {
      const currentName = this.colors[index].name;
      const name = event.target.value.trim() || getColorName(this.colors[index].hex);

      if (name !== currentName && this.hasColorNamed(name, index)) {
        this.makeToast({ variant: 'danger', msg: `This product already has a color named ${name}.` });
      } else {
        this.$set(this.colors, index, { ...this.colors[index], name });
      }

      this.editingColorIndex = null;
    },

    removeColor(index) {
      this.colors.splice(index, 1);
    },

    async uploadImages(files) {
      this.isImgUploading = true;

      try {
        for (const file of files) {
          const urlResp = await this.getProductImageUploadPresignedUrl({
            file_name: file.name,
            content_type: file.type,
            upload_type: 'images',
          });
          await this.uploadToPresignedUrl({
            url: urlResp.upload_url,
            file,
          });

          const imageUrl = urlResp.upload_url.split('?')[0];
          if (!this.images.includes(imageUrl)) {
            this.images.push(imageUrl);
          }
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'One or more images could not be uploaded. Please try again.' });
      } finally {
        this.isImgUploading = false;
      }
    },

    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          const data = {
            product_title: this.name,
            product_description: this.description,
            product_url: this.images[0],
            product_images: this.images,
            colors: this.colors.map(productColor => productColor.hex),
            color_names: this.colors.map(productColor => productColor.name),
            sizes: this.sizeEnable ? (this.sizes[0].length ? this.sizes : null) : null,
            hide_from_store: this.hideFromStore,
            school: this.getLoggedInUser.linked_entity.id,
            price: this.price,
            category: this.category,
          };
          if (this.$route.params.id) {
            await this.updateProduct({
              id: this.$route.params.id,
              data,
            });
            this.makeToast({ variant: 'success', msg: this.$t('Product Updated!') });
          } else {
            await this.createProduct({
              ...data,
            });
            this.makeToast({ variant: 'success', msg: 'Product Added!' });
          }
          setTimeout(
            () =>
              this.$router.push({
                name: 'manage-product-list',
              }),
            250
          );
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.isLoading = true;

    try {
      if (this.$route.params.id) {
        const resp = (await this.getProduct(this.$route.params.id)).data;
        this.name = resp.product_title;
        this.description = resp.product_description;
        this.images = get(resp, 'product_images.length')
          ? resp.product_images
          : resp.product_url
          ? [resp.product_url]
          : [];
        const colors = get(resp, 'colors.length') ? resp.colors : resp.color ? [resp.color] : [];
        const colorNames = get(resp, 'color_names') || [];
        this.colors = colors.map((hex, index) => ({ hex, name: colorNames[index] || getColorName(hex) }));
        this.sizeEnable = get(resp, 'sizes.length') ? true : false;
        this.sizeEnable = get(resp, 'sizes.length') ? true : false;
        this.hideFromStore = resp.hide_from_store;
        this.sizes = resp.sizes ? resp.sizes : [''];
        this.price = resp.price;
        this.category = resp.category;
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({
        name: 'manage-product-list',
      });
    }

    this.isLoading = false;
  },
};
</script>

<style scoped>
.color-chip {
  display: inline-flex;
  align-items: center;
  height: 36px;
  margin: 0 8px 8px 0;
  padding: 0 6px 0 10px;
  border: 1px solid #e0e3e6;
  border-radius: 18px;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: border-color 0.15s ease;
}

.color-chip:focus-within {
  border-color: #adb5bd;
}

.color-chip__dot {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 50%;
  background-color: var(--swatch-color);
  cursor: pointer;
}

.color-chip__label {
  font-size: 13px;
  color: #495057;
  letter-spacing: 0.02em;
}

.color-chip__input {
  min-width: 40px;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #adb5bd;
  background: transparent;
  font-size: 13px;
  color: #495057;
  letter-spacing: 0.02em;
}

.color-chip__input:focus {
  outline: 0;
  border-bottom-color: var(--primary, #007bff);
}

.color-chip__edit {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  padding: 2px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.color-chip__edit:hover {
  color: var(--primary, #007bff);
  background-color: #f8f9fa;
}

.color-chip__remove {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  padding: 2px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.color-chip__remove:hover {
  color: #dc3545;
  background-color: #f8f9fa;
}

.color-chip--add {
  padding: 0 14px 0 10px;
  border-style: dashed;
  color: #6c757d;
  font-size: 13px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.color-chip--add:hover {
  color: var(--primary, #007bff);
  border-color: var(--primary, #007bff);
}

.product-image-preview {
  width: 100%;
  height: 140px;
  object-fit: contain;
}

.product-image-drag-handle {
  cursor: grab;
}
</style>
